import { useMemo, useState } from 'react';
import { useLocation } from 'react-router';
import type { ContentRouteFolderEntry, SiblingFolderLink } from '@/lib/api/content-route-index.server';
import type { ContentFolderSearchResult } from '@/lib/api/content-search.server';
import EatalyCategoryPlpStickyBar from '@/components/eataly-category-plp-sticky-bar';
import EatalyCategoryPlpOrdinationDrawer from '@/components/eataly-category-plp-ordination-drawer';
import ContentFolderListingHeader from '@/components/content-folder-listing/content-folder-listing-header';
import ContentFolderListingGrid from '@/components/content-folder-listing/content-folder-listing-grid';
import { useContentFolderHeaderOptimisticState } from '@/components/content-folder-listing/use-content-folder-header-optimistic-state';
import { useContentFolderListingNavigation } from '@/components/content-folder-listing/use-content-folder-listing-navigation';
import { useFiltersPanelState } from '@/hooks/use-filters-panel-state';
import { useOrdinationPanelState } from '@/hooks/use-ordination-panel-state';
import { useListingPaginationScroll } from '@/hooks/use-listing-pagination-scroll';
import { getNonCriticalSkeletonCount } from '@/lib/search-product-grid';
import {
    getContentFolderMaxNonCriticalSkeletonCount,
    getContentFolderMinLoadingSkeletonCount,
} from '@/lib/content-folder-listing-hits';
import { NEWS_LISTING_DEFAULT_SORT_ID } from '@/lib/content-sort-options';
import { CONTENT_FOLDER_REFINEMENT_ATTRIBUTE_ID } from '@/lib/query-params';
import { selectedContentFolderFilterIds } from '@/lib/content-folder-filter';
import { useMediaQuery } from '@/hooks/use-media-query';
import { useConfig } from '@salesforce/storefront-next-runtime/config';
import { useDeferredRender } from '@/hooks/use-deferred-render';
import { cn } from '@/lib/utils';
import { useTranslation } from 'react-i18next';
import SeoLinkingSection from '@/components/seo-linking/seo-linking-section';
import type { PageWithComponentData } from 'src/lib/page-designer/page-loader.server';
import { Region } from '@/components/region';
import { sanitizeHtml } from '@/lib/sanitize-html';

type ContentFolderListingProps = {
    folder: ContentRouteFolderEntry;
    page?: PageWithComponentData;
    kind: 'folderListing' | 'newsListing';
    criticalSearchResult: ContentFolderSearchResult;
    nonCriticalSearchResult?: Promise<ContentFolderSearchResult>;
    subCategoryLinks: SiblingFolderLink[];
    currentPage: number;
    limit: number;
    initialFiltersOpen: boolean;
    initialOrdinationOpen: boolean;
    sortingOptionIds: string[];
};

export default function ContentFolderListing({
    folder,
    page,
    kind,
    criticalSearchResult,
    nonCriticalSearchResult,
    subCategoryLinks,
    currentPage,
    limit,
    initialFiltersOpen,
    initialOrdinationOpen,
    sortingOptionIds,
}: ContentFolderListingProps) {
    const { t: tSeoLinking } = useTranslation('seoLinking');
    const location = useLocation();
    const { listingTopRef, isListingLoading } = useContentFolderListingNavigation();
    useListingPaginationScroll(listingTopRef);

    const {
        title,
        description,
        showDescriptionSkeleton,
        showSubcategoriesSkeleton,
        showTitleSkeleton,
        setPendingNavigationTarget,
    } = useContentFolderHeaderOptimisticState(folder);
    const { filtersOpen, handleFiltersDrawerOpenChange } = useFiltersPanelState(initialFiltersOpen);
    const [ordinationOpen, toggleOrdinationOpen, handleOrdinationOpenChange] =
        useOrdinationPanelState(initialOrdinationOpen);
    const [pageNumber, setPageNumber] = useState(currentPage);
    const isSquaredCardLayout = folder.c_folderListingLayout === 'squared-card-layout';
    const showCategoryFilter = folder.c_showCategoryFilter === true && subCategoryLinks.length > 0;
    const gridClassName = cn(
        ' pt-6 md:pt-10 pb-8 grid  lg:gap-x-1 ',
        isSquaredCardLayout
            ? 'grid-cols-2 lg:grid-cols-4 gap-y-8 gap-x-1 lg:gap-y-6'
            : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-y-4 lg:gap-y-6'
    );
    const isDesktop = useMediaQuery('(min-width: 1024px)');
    const config = useConfig();
    const maxSkeletonCount = getContentFolderMaxNonCriticalSkeletonCount(
        config.search.content.skeletons,
        isDesktop,
        folder.c_folderListingLayout
    );
    const criticalCount = criticalSearchResult?.hits?.length ?? 0;
    const resolvedNonCriticalCount = getNonCriticalSkeletonCount({
        maxSkeletons: maxSkeletonCount,
        limit,
        criticalCount,
        total: criticalSearchResult?.total ?? 0,
        offset: criticalSearchResult?.offset ?? 0,
    });
    const nonCriticalCount = resolvedNonCriticalCount;
    const loadingSkeletonCount = Math.max(
        (criticalSearchResult.hits?.length ?? 0) + nonCriticalCount,
        getContentFolderMinLoadingSkeletonCount(folder.c_folderListingLayout)
    );
    const shouldRenderNonCritical = useDeferredRender(nonCriticalCount > 0);
    const defaultSortId = kind === 'newsListing' ? NEWS_LISTING_DEFAULT_SORT_ID : undefined;
    const listingRefines = useMemo(() => new URLSearchParams(location.search).getAll('refine'), [location.search]);
    const selectedFolderIds = selectedContentFolderFilterIds(
        listingRefines,
        new Set(subCategoryLinks.map((link) => link.id))
    );

    return (
        <div className="relative" ref={listingTopRef}>
            <ContentFolderListingHeader
                title={title}
                description={description}
                showTitleSkeleton={showTitleSkeleton}
                showDescriptionSkeleton={showDescriptionSkeleton}
                showSubcategoriesSkeleton={showSubcategoriesSkeleton}
                subCategoryLinks={subCategoryLinks}
                onNavigateSubcategory={setPendingNavigationTarget}
                isSquaredCardLayout={isSquaredCardLayout}
                showCategoryFilter={showCategoryFilter}
            />
            {!isSquaredCardLayout && (
                <EatalyCategoryPlpStickyBar
                    result={{
                        total: criticalSearchResult.total,
                        ...(selectedFolderIds.length > 0 && {
                            selectedRefinements: Object.fromEntries(
                                selectedFolderIds.map((folderId) => [
                                    `${CONTENT_FOLDER_REFINEMENT_ATTRIBUTE_ID}=${folderId}`,
                                    folderId,
                                ])
                            ),
                        }),
                    }}
                    refine={listingRefines}
                    filtersOpen={filtersOpen}
                    onFiltersDrawerOpenChange={handleFiltersDrawerOpenChange}
                    filtersAvailable={showCategoryFilter}
                    folderLinks={showCategoryFilter ? subCategoryLinks : undefined}
                    ordinationOpen={ordinationOpen}
                    onToggleOrdination={toggleOrdinationOpen}
                    onOrdinationOpenChange={handleOrdinationOpenChange}
                    sortingOptionIds={sortingOptionIds}
                    defaultSortId={defaultSortId}
                    countLabelKey="categoryPlp:contentFolderListing.itemsAvailable"
                    paginationLimit={limit}
                    pageNumber={pageNumber}
                    isListingDataPending={isListingLoading}
                />
            )}
            <EatalyCategoryPlpOrdinationDrawer
                open={ordinationOpen}
                onOpenChange={handleOrdinationOpenChange}
                result={criticalSearchResult}
                sortingOptionIds={sortingOptionIds}
                defaultSortId={defaultSortId}
            />
            <ContentFolderListingGrid
                gridClassName={gridClassName}
                page={page}
                kind={kind}
                isListingLoading={isListingLoading}
                loadingSkeletonCount={loadingSkeletonCount}
                criticalSearchResult={criticalSearchResult}
                nonCriticalSearchResult={nonCriticalSearchResult}
                shouldRenderNonCritical={shouldRenderNonCritical}
                nonCriticalCount={nonCriticalCount}
                criticalCount={criticalCount}
                currentPage={currentPage}
                limit={limit}
                setPageNumber={setPageNumber}
                cardVariant={isSquaredCardLayout ? 'square' : 'standard'}
            />
            {folder.c_secondaryDescription && (
                <div className="grid-global">
                    <div className="col-span-6 lg:col-start-2 lg:col-span-10 body-copy-l text-center pb-6 lg:pb-12">
                        {/* eslint-disable-next-line react/no-danger -- BM/PIM-authored HTML */}
                        <div dangerouslySetInnerHTML={{ __html: sanitizeHtml(folder.c_secondaryDescription ?? '') }} />
                    </div>
                </div>
            )}
            {kind === 'newsListing' && page && <Region page={page} regionId="pressOffice" />}
            <SeoLinkingSection
                title={tSeoLinking('sectionTitle')}
                navAriaLabel={tSeoLinking('sectionTitle')}
                links={subCategoryLinks.map((link) => ({
                    name: link.title,
                    id: link.id,
                    to: link.path,
                }))}
            />
        </div>
    );
}
